import React, { useState, useEffect } from 'react';
import { Row, Col, Spinner, Card, Nav, Collapse, Navbar, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import Graph from '@graphs/SingleStation/Graph';
import GraphActions from '@graphs/SingleStation/Actions';
import DatasetDropdown from '@graphs/SingleStation/Dropdown/Dataset';
import DatasetLegend from '@graphs/SingleStation/Legend';
import { GRAPH_TAB_KEY, STATE_STATUS, FROST_PROBE_CONFIG, DATASET_TYPES } from '@awp/constants';
import FrostProbe from './FrostProbe';

import { selectStatus, selectData, selectHeatMapObservation } from '@stores/dataVisualizationSlice';
import {
  currentGraphView,
  getSelectedDatasetOptions,
  setSelectedDatasets,
  shouldShowGraphLegends,
} from '@stores/graphViewPageSlice';

const GraphContainer = ({ id }) => {
  const dispatch = useDispatch();
  const [tabKey, setTabKey] = useState(GRAPH_TAB_KEY.TIME_SERIES);
  const graphKey = id.slice(0, id.lastIndexOf('_'));
  const status = useSelector(selectStatus);
  const data = useSelector(selectData);
  const showGraphLegends = useSelector(shouldShowGraphLegends);
  const heatMapObservation = useSelector(selectHeatMapObservation);

  const graphView = useSelector(currentGraphView);

  const selectedDatasets = useSelector((state) => getSelectedDatasetOptions(state, graphKey));
  const dispatchDatasetOptions = (options, isUpdateAction) =>
    dispatch(setSelectedDatasets({ options: options, graphId: graphKey, isUpdateAction: isUpdateAction }));

  const [showLegend, setShowLegend] = useState(showGraphLegends);

  const graphWidth = showLegend ? 9 : 12;
  const legendWidth = showLegend ? 3 : 0;

  const graphStyle = `container-transition${showLegend ? ' delay-graph' : ''}`;
  const legendStyle = `container-transition${!showLegend ? ' delay-legend' : ''}`;
  const legendLinkStyle = { textDecoration: 'none' };
  const navBarStyle = { justifyContent: 'space-between' };
  const navStyle = { borderBottom: '0' };
  const legendToggleIconStyle = { fontSize: '1.5rem', color: 'black' };

  useEffect(() => {
    if (status.data === STATE_STATUS.SUCCESS) {
      //this is where we set the selected datasets using the graphView graph.dataSets
      // datasets are added/removed from the graph view graph.dataSets and the view is
      // marked as dirty graphView.isModified which is what flags it for save/update
      const graphDatasets =
        graphView.DataGraphs.find((g) => g.DataGraphId.toString() === graphKey).PersistentGraphFilters || [];

      if (status.data === STATE_STATUS.SUCCESS) {
        let results = graphDatasets.map((ds) => {
          return data.Datasets.find(
            ({ PlacementName, observationTypeId }) =>
              PlacementName === ds.PlacementName && parseInt(observationTypeId) === parseInt(ds.ObservationTypeId),
          );
        });

        results = results.filter((e) => e !== undefined);

        const options = results.map((ds) => {
          return {
            group: ds.group,
            typeId: ds.observationTypeId,
            value: ds.DatasetId,
            label: ds.label,
            color: ds.graphConfig.graphColor || '#000',
          };
        });

        //need to rebuild the frostprobe option
        const frostProbeObservations = graphDatasets.find(
          (ds) => ds.ObservationTypeId === heatMapObservation.ObservationTypeId,
        );
        if (frostProbeObservations) {
          const subSfcObsTypeId =
            data.Datasets.find(
              (ds) =>
                ds.type === DATASET_TYPES.FrostProbe &&
                ds.graphConfig.observationTypeName === FROST_PROBE_CONFIG.SUBSFC_TEMP,
            )?.observationTypeId || 0;

          const group = data.Datasets.filter((dataset) => dataset.observationTypeId === subSfcObsTypeId)
            .map((d) => d.PlacementName)
            .filter((v, i, s) => s.indexOf(v) === i)[0];

          if (subSfcObsTypeId !== 0) {
            options.push({
              group: group,
              typeId: heatMapObservation.ObservationTypeId,
              value: `0_${heatMapObservation.ObservationTypeId}_${subSfcObsTypeId}`,
              label: heatMapObservation.ObservationTypeDisplayName,
            });
          }
        }

        dispatch(setSelectedDatasets({ options: options, graphId: graphKey }));
      }
    }
  }, [data, graphView.DataGraphs]);

  useEffect(() => {
    //a useEffect for setting the page level flag to show/hide all legends
    // this will set the container level state and force a re-render
    setShowLegend(showGraphLegends);
  }, [showGraphLegends]);

  return (
    <Card border="light" className="page-card">
      <Card.Body>
        <Row>
          <Col>
            <GraphActions graphId={id} />
          </Col>
        </Row>
        <Navbar expand style={navBarStyle}>
          <Nav style={navStyle} variant="tabs" activeKey={tabKey} onSelect={(k) => setTabKey(k)} className="mb-3 tab">
            <Nav.Item>
              <Nav.Link style={legendLinkStyle} eventKey={GRAPH_TAB_KEY.TIME_SERIES}>
                Time Series
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link style={legendLinkStyle} eventKey={GRAPH_TAB_KEY.TABLE_VIEW}>
                Table View
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link style={legendLinkStyle} eventKey={GRAPH_TAB_KEY.WIND_ROSE} disabled>
                Wind Rose
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link style={legendLinkStyle} eventKey={GRAPH_TAB_KEY.FROST_PROBE}>
                Frost Probe
              </Nav.Link>
            </Nav.Item>
          </Nav>
          {tabKey == GRAPH_TAB_KEY.TIME_SERIES || tabKey == GRAPH_TAB_KEY.TABLE_VIEW ? (
            <Nav>
              <OverlayTrigger
                placement="left"
                overlay={<Tooltip id={`tooltip-${id}`}>{showLegend ? 'Hide Legend' : 'Show Legend'}</Tooltip>}
              >
                <Nav.Item className="justify-content-end">
                  <i
                    style={legendToggleIconStyle}
                    className={`bi ${showLegend ? 'bi-box-arrow-right' : 'bi-box-arrow-left'}`}
                    onClick={() => setShowLegend(!showLegend)}
                  ></i>
                </Nav.Item>
              </OverlayTrigger>
            </Nav>
          ) : null}
        </Navbar>
        <div className="graph__container">
          {tabKey == GRAPH_TAB_KEY.TIME_SERIES || tabKey == GRAPH_TAB_KEY.TABLE_VIEW ? (
            <>
              <Row>
                <Col lg={graphWidth} md={graphWidth} className={graphStyle}>
                  <Graph graphId={id} presentation={tabKey} />
                </Col>
                <Collapse in={showLegend}>
                  <Col lg={legendWidth} md={legendWidth} className={legendStyle}>
                    <div id={id + '_Legend'} className="legend__container">
                      {status.data === STATE_STATUS.SUCCESS ? (
                        <div id={id + '_Legend'} className="legend__container">
                          <DatasetLegend
                            graphId={id}
                            setSelectedOptions={dispatchDatasetOptions}
                            selectedOptions={selectedDatasets}
                          />
                          <DatasetDropdown
                            withImages={true}
                            setSelectedOptions={dispatchDatasetOptions}
                            selectedOptions={selectedDatasets}
                            graphId={id}
                            className="legend__dropdown"
                          />
                        </div>
                      ) : (
                        <div className="legend__loading">
                          <Spinner animation="border" />
                        </div>
                      )}
                    </div>
                  </Col>
                </Collapse>
              </Row>
            </>
          ) : tabKey == GRAPH_TAB_KEY.FROST_PROBE ? (
            <Row>
              <Col lg={12} md={12}>
                {status.data === STATE_STATUS.SUCCESS ? (
                  <FrostProbe graphId={`${id}_FP`} />
                ) : (
                  <div className="frostprobe_Loading">
                    <Spinner animation="border" />
                  </div>
                )}
              </Col>
            </Row>
          ) : null}
        </div>
      </Card.Body>
    </Card>
  );
};

GraphContainer.propTypes = {
  id: PropTypes.any.isRequired,
};

export default GraphContainer;
